import React, { useState } from 'react';
import { connect } from 'react-redux';
import { AppState } from '../store/state';
import { AppDispatch } from '../store/store';
import {
  selectAssignmentState,
  selectConference,
  selectCurrentRole,
  selectEmailState,
  selectFormState,
  selectSubmissionState,
  selectPaperStatusState,
} from '../store/selectors';
import { Role, ConferenceState } from '../store/conference/types';
import { getRoleName } from '../helpers/translations';
import { getSupervisedRoleIds } from '../helpers/form';
import { FormState } from '../store/form/types';
import { useTranslation } from 'react-i18next';
import { EmailRecipientType, EmailSendDTO, EmailTemplate, PlaceholderByRecipientType } from '../store/email/types';
import emailSlice from '../store/email/slice';
import EmailForm from '../components/EmailForm/EmailForm';
import { Review } from '../store/review/types';
import { CategoryEnum } from '../store/paper-status/types.d';
import styled from 'styled-components';
import { apiCall } from '../store/api';
import { baseURL } from '../store/root-saga';

type FilterMode = 'all' | 'unfinished';

interface RoleSelection {
  selected: boolean;
  filter: FilterMode;
}

type SendState = 'compose' | 'sending' | 'done' | 'error';

interface Props {
  currentRole: Role | null;
  conferenceState: ConferenceState;
  assignments: Review[];
  formStoreState: FormState;
  placeholdersByRecipientType: PlaceholderByRecipientType;
  templatesById: { [key: number]: EmailTemplate };
  submissionsById: { [key: string]: Submission };
  paperStatusById: { [key: number]: PaperStatus };
}

const BulkEmailReviewersPage: React.FC<Props> = ({
  currentRole,
  conferenceState,
  assignments,
  formStoreState,
  placeholdersByRecipientType,
  templatesById,
  submissionsById,
  paperStatusById,
}) => {
  const { t } = useTranslation();

  const supervisedRoleIds = currentRole
    ? getSupervisedRoleIds(formStoreState.formById, formStoreState.visibilityByFormId, currentRole.id)
    : [];
  const emailRoleIds = conferenceState.roleRelationsById['send-email']?.[currentRole?.id ?? 0] ?? [];
  const inProgressSubmissionIds = new Set(
    Object.values(submissionsById)
      .filter((s) => {
        const ps = paperStatusById[s.paper_status_id];
        return ps && ps.category === CategoryEnum.In_Progress;
      })
      .map((s) => s.id),
  );

  const supervisedRoles = supervisedRoleIds
    .filter((id) => emailRoleIds.includes(id))
    .map((id) => conferenceState.roleById[id])
    .filter((r): r is Role => !!r)
    .filter((role) => assignments.some((a) => a.role_id === role.id && inProgressSubmissionIds.has(a.submission_id)));

  const [roleSelections, setRoleSelections] = useState<{ [roleId: number]: RoleSelection }>(() => {
    const initial: { [roleId: number]: RoleSelection } = {};
    supervisedRoles.forEach((role) => {
      initial[role.id] = { selected: true, filter: 'all' };
    });
    return initial;
  });

  const recipientType: EmailRecipientType = 'submission:ids';

  const getFilteredAssignments = (): Review[] => {
    const filtered: Review[] = [];
    supervisedRoles.forEach((role) => {
      const selection = roleSelections[role.id];
      if (!selection?.selected) return;

      const roleAssignments = assignments.filter(
        (a) => a.role_id === role.id && inProgressSubmissionIds.has(a.submission_id),
      );
      if (selection.filter === 'unfinished') {
        filtered.push(...roleAssignments.filter((a) => a.validation_status === 'Pending'));
      } else {
        filtered.push(...roleAssignments);
      }
    });
    return filtered;
  };

  const getSubmissionGroups = (): { submissionId: number; recipientIds: number[] }[] => {
    const bySubmission: { [submissionId: number]: Set<number> } = {};
    getFilteredAssignments().forEach((a) => {
      if (!bySubmission[a.submission_id]) {
        bySubmission[a.submission_id] = new Set();
      }
      bySubmission[a.submission_id].add(a.person_role_id);
    });
    return Object.entries(bySubmission).map(([submissionId, personRoleIds]) => ({
      submissionId: parseInt(submissionId),
      recipientIds: Array.from(personRoleIds),
    }));
  };

  const [email, setEmail] = useState<EmailSendDTO>({
    ids: [],
    subject: '',
    body: '',
    dry_run: false,
    recipient_type: recipientType,
  });

  const [sendState, setSendState] = useState<SendState>('compose');
  const [progress, setProgress] = useState({ sent: 0, total: 0, errors: 0 });

  const submissionGroups = getSubmissionGroups();
  const isValid = !!email.subject && !!email.body && submissionGroups.length > 0;

  const updateRoleSelection = (roleId: number, update: Partial<RoleSelection>) => {
    setRoleSelections((prev) => ({
      ...prev,
      [roleId]: { ...prev[roleId], ...update },
    }));
  };

  const handleSend = async () => {
    const groups = getSubmissionGroups();
    setSendState('sending');
    setProgress({ sent: 0, total: groups.length, errors: 0 });

    let errors = 0;
    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      const payload: EmailSendDTO = {
        ids: [group.submissionId],
        subject: email.subject,
        body: email.body,
        dry_run: false,
        recipient_type: 'submission:ids',
        recipient_ids: group.recipientIds,
        template_id: email.template_id,
      };

      try {
        const result = await apiCall(`${baseURL}/api/emails`, {
          method: 'POST',
          auth: true,
          role: true,
          body: JSON.stringify(payload),
        });
        if (result.type !== 'ok') {
          errors++;
        }
      } catch {
        errors++;
      }

      setProgress({ sent: i + 1, total: groups.length, errors });
    }

    setSendState(errors > 0 ? 'error' : 'done');
  };

  const handleReset = () => {
    setSendState('compose');
    setProgress({ sent: 0, total: 0, errors: 0 });
  };

  if (!currentRole || supervisedRoles.length === 0) {
    return (
      <div className="p-6">
        <p>{t('No reviewer roles to supervise.')}</p>
      </div>
    );
  }

  return (
    <StyledWrapper className="flex flex-col h-full pb-6">
      <div className="p-6">
        <h2 className="text-xl font-bold mb-6">{t('Bulk email reviewers')}</h2>

        {sendState === 'compose' && (
          <>
            <div className="mb-6">
              <h3 className="text-base font-semibold mb-3">{t('Select reviewer roles')}</h3>
              {supervisedRoles.map((role) => {
                const selection = roleSelections[role.id];
                return (
                  <div key={role.id} className="mb-4 p-4 border rounded bg-gray-50">
                    <label className="flex items-center gap-2 mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selection?.selected ?? false}
                        onChange={(e) => updateRoleSelection(role.id, { selected: e.target.checked })}
                      />
                      <span className="font-medium">{getRoleName(role, t)}</span>
                    </label>
                    {selection?.selected && (
                      <div className="ml-6 flex gap-4">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`filter-${role.id}`}
                            checked={selection.filter === 'all'}
                            onChange={() => updateRoleSelection(role.id, { filter: 'all' })}
                          />
                          {t('All reviewers')}
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`filter-${role.id}`}
                            checked={selection.filter === 'unfinished'}
                            onChange={() => updateRoleSelection(role.id, { filter: 'unfinished' })}
                          />
                          {t('Only reviewers with unfinished reviews')}
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <EmailForm
              onChange={(value) => setEmail((prev) => ({ ...prev, ...value }))}
              placeholders={placeholdersByRecipientType[recipientType] ?? []}
              value={email}
              templatesById={templatesById}
              hideTemplates={currentRole?.type != 'chair'}
            />

            <div className="mt-4">
              <button
                className="bg-blue-600 text-white px-6 py-2 rounded disabled:opacity-50"
                disabled={!isValid}
                onClick={handleSend}
              >
                {t('Send')} ({submissionGroups.length} {t('submissions')})
              </button>
            </div>
          </>
        )}

        {sendState === 'sending' && (
          <div>
            <p className="mb-2 font-medium">{t('Sending emails...')}</p>
            <div className="w-full bg-gray-200 rounded h-4 mb-2">
              <div
                className="bg-blue-600 h-4 rounded transition-all"
                style={{ width: `${progress.total > 0 ? (progress.sent / progress.total) * 100 : 0}%` }}
              />
            </div>
            <p className="text-sm text-gray-600">
              {progress.sent} / {progress.total} {t('submissions')}
              {progress.errors > 0 && (
                <span className="text-red-500 ml-2">
                  ({progress.errors} {t('errors')})
                </span>
              )}
            </p>
          </div>
        )}

        {(sendState === 'done' || sendState === 'error') && (
          <div>
            <p className={`mb-4 font-medium ${sendState === 'done' ? 'text-green-600' : 'text-orange-500'}`}>
              {sendState === 'done'
                ? t('All emails sent successfully.')
                : t('Sending completed with {{errors}} errors.', { errors: progress.errors })}
            </p>
            <p className="mb-4 text-sm text-gray-600">
              {progress.sent} / {progress.total} {t('submissions')}
            </p>
            <button className="bg-blue-600 text-white px-6 py-2 rounded" onClick={handleReset}>
              {t('Send more')}
            </button>
          </div>
        )}
      </div>
    </StyledWrapper>
  );
};

const mapStateToProps = (state: AppState) => ({
  currentRole: selectCurrentRole(state),
  conferenceState: selectConference(state),
  assignments: selectAssignmentState(state).assignments,
  formStoreState: selectFormState(state),
  placeholdersByRecipientType: selectEmailState(state).placeholdersByRecipientType,
  templatesById: selectEmailState(state).templatesById,
  submissionsById: selectSubmissionState(state).submissionsById,
  paperStatusById: selectPaperStatusState(state).paperStatusById,
});

export default connect(mapStateToProps)(BulkEmailReviewersPage);

const StyledWrapper = styled.div`
  overflow-y: auto;
`;
